/**
 * Page de vérification d'email à l'inscription
 * Appelée automatiquement via le lien reçu par mail : /verify-email?email=...&code=...
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../infrastructure/api/authApi';

type Status = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const email = searchParams.get('email') ?? '';
    const code = searchParams.get('code') ?? '';

    if (!email || !code) {
      setStatus('error');
      setMessage('Lien de vérification invalide (paramètres manquants).');
      return;
    }

    authApi
      .verifyEmail({ email, code })
      .then(() => {
        setStatus('success');
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error ? err.message : 'Une erreur est survenue lors de la vérification.';
        setStatus('error');
        setMessage(msg);
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-raised py-12 px-4">
      <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8 text-center space-y-6">
        {status === 'loading' && (
          <>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
            <p className="text-content-muted">Vérification en cours…</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-green-500 text-5xl">✓</div>
            <h2 className="text-2xl font-bold text-content">
              Email vérifié avec succès
            </h2>
            <p className="text-content-muted">
              Votre compte est maintenant actif. Vous pouvez vous connecter.
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="w-full py-2 px-4 bg-primary hover:bg-primary-hover text-content-inverted font-medium rounded-md transition-colors"
            >
              Se connecter
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-red-500 text-5xl">✗</div>
            <h2 className="text-2xl font-bold text-content">
              Vérification échouée
            </h2>
            <p className="text-content-muted">{message}</p>
            <button
              onClick={() => navigate('/auth/login')}
              className="w-full py-2 px-4 bg-primary hover:bg-primary-hover text-content-inverted font-medium rounded-md transition-colors"
            >
              Retour à la connexion
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmailPage;
